/* eslint-disable no-param-reassign */
import { BaseModel } from '@/pages/datalab/components/SortableDrag/baseModel';
import type { IColumnInfo, IColumnRawInfo } from '@dbl/core/dataSource';
import { DBColumnType, DragState, ErrorDrag, numberType } from '@/pages/datalab/components/SortableDrag/typeDefine';
import moment from 'moment';
import { ITableJoinRelation } from '../Analysis/components/HeaderData/interface';
import type {
  IGetTableInfoDto,
  ITableColumnClassDto,
} from '@dbl/services/dataSource';
import {
  convertTableColumnToColumnRawInfo,
  getTableInfoList,
} from '@dbl/services/dataSource';
import type { TEnrichedTableColumn } from '../types/tableColumnEnriched';
import { FiltersItem } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import {
  createNumberPackage,
  getNumberPackageDetail,
  type ICreateNumberPackageRequest,
} from '@dbl/services/numberPackage';
import { checkNullString } from '@/pages/datalab/utils/utils';
import { DateGranularity } from '@dbl/core/date';
import * as crypto from 'crypto-js';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const namespace = 'Analysis/PacketExtraction';
const packageModel = new BaseModel(namespace);

const service = { getTableInfoList };
export default {
  namespace,
  state: {
    dataTables: [] as IGetTableInfoDto[],
    dataTableJoinRelations: [] as ITableJoinRelation[], // 表之间的关系
    columnFilters: [] as IColumnInfo[],
    sensitiveColumns: [] as IColumnInfo[],
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    dragState: {
      currentDrag: '',
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errorDrag: {} as ErrorDrag,
    } as DragState,
    saveObj: {
      package_uuid: '',
      game_code: '',
      filters: '',
      name: '',
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      column_infos: {} as IColumnInfo,
      type: '0',
      remark: '',
      table_infos: '',
    },
    previousHashForSave: '', // 上次计算的条件hash(用于保存)
    hashEqualForSave: true, // 条件hash是否改变(用于保存)
    mode: ChartCalculateMode.Aggregate
  },
  reducers: {
    ...packageModel.reducers,
    save(state: any, action: { payload: any; }) {
      const {
        columnFilters,
      }: {
        columnFilters: IColumnInfo[];
      } = { ...state, ...action.payload };

      let newHash = '';
      const conditionStr = JSON.stringify(columnFilters || []);
      if (conditionStr.length > 0) {
        // newHash = crypto.createHash('sha256').update(conditionStr).digest('base64');
        newHash = crypto.enc.Base64.stringify(crypto.SHA256(conditionStr));
      }

      // 判断是否出现离开时弹出保存弹框

      const { calculateHashForSave } = action.payload;
      const { previousHashForSave } = state;
      let hashStateForSave: any;
      if (calculateHashForSave === true) {
        hashStateForSave = { previousHashForSave: newHash, hashEqualForSave: true };
      } else {
        const hashEqualForSave = previousHashForSave === newHash;
        hashStateForSave = { hashEqualForSave };
      }
      return { ...state, ...action.payload, ...hashStateForSave };
    },
    saveDrag(state: any, action: { payload: any; }) {
      const { dragState: dragStatePayload } = action.payload;
      if (dragStatePayload) {
        return { ...state, ...{ dragState: dragStatePayload } };
      }
      const { dragState } = state;
      const { currentDrag, errorDrag } = dragState;
      const value = { currentDrag: '', errorDrag: { ...errorDrag, [currentDrag]: '' } };
      return { ...state, ...{ dragState: value } };
    },
  },
  effects: {
    ...packageModel.effects,
    *saveCondition(
      { payload, callback }: { payload: any; callback: Function; },
      { put, select }: { put: any; select: any; },
    ): any {
      const {
        dataTableJoinRelations,
        columnFilters,
        dataTables,
      }: { dataTables: IGetTableInfoDto[]; dataTableJoinRelations: ITableJoinRelation[]; columnFilters: IColumnInfo[]; } =
        yield select((state: { [x: string]: any; }) => state[namespace]);

      const { params } = payload;

      const newFilters: FiltersItem[] = (columnFilters || []).map((item) => ({
        table_id: item.table_id,
        column: item.column_name,
        operation: item.operation ? item.operation : '',
        value: item.column_value ? item.column_value : [],
      }));

      let paramReq: ICreateNumberPackageRequest = {
        game_code: params.game_code,
        filter: JSON.stringify({ filters: newFilters }),
        table_infos: JSON.stringify(dataTableJoinRelations),
        name: params.name,
        type: Number(params.type),
        remark: params.remark,
        create_time: moment().format('YYYY-MM-DD HH:mm:ss'),
      };
      if (params.package_uuid) {
        paramReq = {
          ...paramReq,
          package_uuid: Number(params.package_uuid),
        };
      }

      if (!checkNullString(params.sensitiveValue)) {
        const [selectTableId, selectColumnName] = params.sensitiveValue.split('.');
        let sensitiveColumn: {} | undefined = undefined;
        dataTables.forEach((item) =>
          item.column_list?.forEach((column) => {
            if (item.table_id === selectTableId && column.name === selectColumnName) {
              sensitiveColumn = {
                column: column.name,
                table_id: item.table_id,
                label: column.display_name,
                column_type: column.type,
              };
            }
          }),
        );
        if (sensitiveColumn) {
          paramReq = {
            ...paramReq,
            column_infos: JSON.stringify(sensitiveColumn),
          };
        }
      }
      const resp = yield createNumberPackage(paramReq);
      if (resp.code === 0) {
        yield put({
          type: 'save',
          payload: {
            calculateHashForSave: true,
          },
        });
      }
      // 如果保存成功就跳转
      if (callback) {
        callback(paramReq, resp);
      }
    },

    *getTableInfo(
      { payload, callback }: { payload: any; callback: Function; },
      { put, select }: { put: any; select: any; },
    ): any {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { package_uuid, tableIds, gameCode }: { tableIds: string[]; package_uuid: number; gameCode: string; } =
        payload;
      let { dataTableJoinRelations } = yield select((state: { [x: string]: any; }) => state[namespace]);
      let tableRsp: any;
      let packageRsp: any;
      if (package_uuid) {
        packageRsp = yield getNumberPackageDetail({
          package_uuid: String(package_uuid),
          game_code: gameCode,
        });
        dataTableJoinRelations = JSON.parse(packageRsp.table_infos);
        const tableIdList = dataTableJoinRelations ? dataTableJoinRelations.map((item: any) => item.table_id) : [];
        tableRsp = yield service.getTableInfoList(tableIdList, gameCode);
      } else if (package_uuid && tableIds.length > 0) {
        tableRsp = yield service.getTableInfoList(tableIds, gameCode);
        dataTableJoinRelations = [
          {
            table_id: tableIds[0],
            table_code: 't0',
            only_one_table: true,
            granularity: tableRsp.find((item: any) => item.table_id === tableIds[0])?.cycle ?? DateGranularity.DAY,
          },
        ];
      }

      if (!tableRsp) {
        if (callback) {
          callback(packageRsp);
        }
        return;
      }
      yield put({
        type: 'save',
        payload: {
          dataTableJoinRelations: dataTableJoinRelations || [],
        },
      });
      yield put({
        type: 'setTableInfo',
        payload: {
          response: tableRsp,
        },
      });
      if (callback) {
        callback(packageRsp);
      }
    },

    *setTableInfo(
      { payload, callback }: { payload: any; callback: Function; },
      { put, select }: { put: any; select: any; },
    ): any {
      const { response } = payload;
      const dataTables: IGetTableInfoDto[] = [];
      const { dataTableJoinRelations }: { dataTableJoinRelations: ITableJoinRelation[]; } = yield select(
        (state: { [x: string]: any; }) => state[namespace],
      );
      const sensitiveColumns: IColumnInfo[] = [];
      const onlyOneTable: boolean = response?.length === 1;
      (response || []).forEach((tableInfoRes: any, index: number) => {
        const tableCode = dataTableJoinRelations.find((item) => item.table_id === tableInfoRes.table_id)?.table_code;
        const tableInfo: IGetTableInfoDto = {
          only_one_table: onlyOneTable,
          table_id: tableInfoRes.table_id,
          table_code: tableCode ? tableCode.toLowerCase() : `t${index}`,
          table_name: tableInfoRes?.table_name,
          customize_name: tableInfoRes?.customize_name,
          customize_name_en: tableInfoRes?.customize_name_en,
          table_desc: tableInfoRes?.table_desc,
          cycle: tableInfoRes?.cycle,
          creator: tableInfoRes?.creator,
          label: tableInfoRes?.label,
          sub_label: tableInfoRes?.sub_label,
          hide: false,
          column_list: tableInfoRes?.column_list,
          column_class_list: [],
        };
        let tableColunmClass: ITableColumnClassDto[] = [
          {
            name: '公共',
            name_en: 'Public',
            columns: [],
          },
          {
            name: '维度',
            name_en: 'Dimension',
            columns: [],
          },
          {
            name: '指标',
            name_en: 'Metric',
            columns: [],
          },
        ];
        (tableInfoRes?.column_list || []).forEach((colunm: TEnrichedTableColumn) => {
          if (colunm.is_common_dimension) {
            if (numberType.values.includes(colunm.type)) {
              colunm.type = 'string';
            }
            colunm.isCommonDimension=true;
            tableColunmClass[0].columns.push(convertTableColumnToColumnRawInfo(colunm, tableInfo, index));
          }
          if (colunm.is_dimension) {
            if (numberType.values.includes(colunm.type)) {
              colunm.type = 'string';
            }
            tableColunmClass[1].columns.push(convertTableColumnToColumnRawInfo(colunm, tableInfo, index));
          }
          if (colunm.is_metric) {
            tableColunmClass[2].columns.push(convertTableColumnToColumnRawInfo(colunm, tableInfo, index));
          }
          if (colunm.is_sensitive) {
            sensitiveColumns.push(convertTableColumnToColumnRawInfo(colunm, tableInfo, index));
          }
        });
        tableColunmClass = tableColunmClass.filter((item) => item.columns.length > 0);
        tableInfo.column_class_list = tableColunmClass;

        dataTables.push(tableInfo);
      });
      dataTables.sort((a: IGetTableInfoDto, b: IGetTableInfoDto) =>
        (a.table_code ? a.table_code : '') < (b.table_code ? b.table_code : '') ? -1 : 1,
      );
      yield put({
        type: 'save',
        payload: {
          dataTables,
          sensitiveColumns,
        },
      });
      if (callback) {
        callback();
      }
    },

    *getPackageDetail({ payload }: { payload: any; }, { put, select }: { put: any; select: any; }): any {
      try {
        const { response } = payload;
        const { dataTables, columnFilters } = yield select((state: { [x: string]: any; }) => state[namespace]);
        const { filters } = JSON.parse(response.filter);
        const newColumnFilters: IColumnInfo[] = [];

        dataTables.forEach((tableInfo: IGetTableInfoDto) => {
          const tableId = tableInfo.table_id;
          tableInfo.column_class_list?.forEach((tableItem: ITableColumnClassDto) => {
            tableItem.columns.forEach((item: IColumnRawInfo) => {
              const filtersResult = filters.find(
                (filterItem: FiltersItem) => filterItem.column === item.column_name && filterItem.table_id === tableId,
              );
              if (filtersResult) {
                let newFilter: IColumnInfo = {
                  ...item,
                  operation: filtersResult.operation,
                  column_value: filtersResult.value,
                };
                newFilter = {
                  ...newFilter,
                  operation: DBColumnType.getFilterOperation(newFilter),
                  column_value: DBColumnType.getFilterValues(newFilter),
                };
                newColumnFilters.push(newFilter);
              }
            });
          });
        });

        const oldColumnInfos = JSON.parse(response.column_infos);
        const columnInfos = { table_id: oldColumnInfos.table_id, column_name: oldColumnInfos.column };

        // 过滤
        yield put({
          type: 'save',
          payload: {
            saveObj: {
              ...response,
              type: `${response.type}`,
              column_infos: columnInfos,
            },
            columnFilters: [
              ...columnFilters,
              ...newColumnFilters.filter(
                (item) => !columnFilters.find((filterItem: IColumnInfo) => filterItem.column_name === item.column_name),
              ),
            ],
            calculateHashForSave: true,
          },
        });

        yield put({
          type: 'initColunmOptions',
          payload: {
            columnFilters: newColumnFilters,
            sortableKey: 'columnFilters',
          },
        });
      } catch (e) {
        console.error(e);
      }
    },

    *clearQuery({ callback }: { payload: any; callback: Function; }, { put }: { put: any; select: any; }): any {
      yield put({
        type: 'save',
        payload: {
          columnFilters: [],
          saveObj: {},
        },
      });

      if (callback) {
        callback();
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    *clearTableInfo({ callback }: { payload: any; callback: Function; }, { put }: { put: any; select: any; }): any {
      yield put({
        type: 'save',
        payload: {
          dataTables: [],
          sensitiveColumns: [],
        },
      });
    },

    *filterConditionByTable({ payload }: { payload: any; }, { put, select }: { put: any; select: any; }): any {
      const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        table_ids,
        excludeTable = false,
        isClearRelations = false,
      }: { table_ids: any[]; excludeTable: boolean; isClearRelations: boolean; } = payload;
      const {
        dataTables,
        dataTableJoinRelations,
        columnFilters,
      }: {
        dataTables: IGetTableInfoDto[];
        dataTableJoinRelations: ITableJoinRelation[];
        columnFilters: IColumnInfo[];
      } = yield select((state: { [x: string]: any; }) => state[namespace]);
      const includeTableIds: string[] = [];
      if (excludeTable) {
        includeTableIds.push(
          ...dataTables.filter((item) => !table_ids.includes(item.table_id)).map((item) => item.table_id),
        );
      } else {
        includeTableIds.push(...table_ids);
      }
      yield put({
        type: 'save',
        payload: {
          dataTables: dataTables.filter((item) => includeTableIds.includes(item.table_id)),
          dataTableJoinRelations: dataTableJoinRelations
            .filter((item) => includeTableIds.includes(item.table_id))
            .map((dataTableJoinRelations) => {
              if (isClearRelations) {
                dataTableJoinRelations.connect_type = '';
                dataTableJoinRelations.on = [];
              }
              return dataTableJoinRelations;
            }),
          columnFilters: columnFilters.filter((item: IColumnInfo) => includeTableIds.includes(item.table_id)),
          table: {}, // table 数据
          chartData: {}, // 折线图和柱状图数据
        },
      });
    },
  },
};
